<script setup lang="ts">
import { computed } from 'vue'

import { formatDateTimeLong } from '../../formatters/date.formatter'
import { formatNumber } from '../../formatters/number.formatter'
import type { Segment } from '../../types/segments.types'
import { isSegmentGenerating } from '../../utils/segment.utils'
import BoxComponent from '../atoms/BoxComponent.vue'
import FlexComponent from '../atoms/FlexComponent.vue'
import TooltipComponent from '../atoms/TooltipComponent.vue'
import TypographyComponent from '../atoms/TypographyComponent.vue'

const props = defineProps<{ segment: Segment }>()

const typeLabel = computed(() => (props.segment.type === 'dynamic' ? 'Dynamic' : 'Static'))
const isGenerating = computed(() => isSegmentGenerating(props.segment.refreshStatus))
const isFailed = computed(() => props.segment.refreshStatus === 'failed')
const lastRefreshedLabel = computed(() => (props.segment.lastRefreshedAt ? formatDateTimeLong(props.segment.lastRefreshedAt) : null))
const createdLabel = computed(() => formatDateTimeLong(props.segment.createdAt))
const updatedLabel = computed(() => formatDateTimeLong(props.segment.updatedAt))
const memberCount = computed(() => formatNumber(props.segment.memberCount, 'compact'))
const memberCountLong = computed(() => formatNumber(props.segment.memberCount, 'standard'))
</script>
<template>
  <BoxComponent :p="24" class="segment-list-item" data-testid="segment-list-item">
    <FlexComponent :gap="16" align="center" wrap="wrap">
      <FlexComponent :gap="12" align="center" class="segment-list-item__main">
        <BoxComponent v-if="isGenerating" class="segment-list-item__badge" role="status">
          <TypographyComponent variant="label-medium" color="var(--inverted)" white-space="nowrap">Generating…</TypographyComponent>
        </BoxComponent>
        <TooltipComponent v-else-if="isFailed" placement="top">
          <BoxComponent class="segment-list-item__badge segment-list-item__badge--failed" role="status">
            <TypographyComponent variant="label-medium" color="var(--inverted)" white-space="nowrap">Failed</TypographyComponent>
          </BoxComponent>
          <template #content>
            <TypographyComponent variant="body-small">
              {{ segment.lastRefreshError ?? 'Refresh failed' }}
            </TypographyComponent>
          </template>
        </TooltipComponent>
        <FlexComponent direction="column" :gap="4" class="segment-list-item__text">
          <TypographyComponent variant="title-large" text-truncate class="segment-list-item__name">
            {{ segment.name }}
          </TypographyComponent>
          <TypographyComponent
            v-if="segment.description"
            variant="body-medium"
            color="var(--secondary-48, #909090)"
            text-truncate
            class="segment-list-item__description"
          >
            {{ segment.description }}
          </TypographyComponent>
          <TypographyComponent
            v-if="lastRefreshedLabel"
            variant="body-small"
            color="var(--secondary-48, #909090)"
            white-space="nowrap"
            class="segment-list-item__refreshed"
          >
            Last refreshed {{ lastRefreshedLabel }}
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
      <FlexComponent :gap="16" align="center" wrap="wrap" class="segment-list-item__meta">
        <FlexComponent direction="column" align="center" :gap="4" class="segment-list-item__cell">
          <TypographyComponent variant="label-medium" color="var(--secondary-48, #909090)">Creators</TypographyComponent>
          <TooltipComponent :disabled="memberCount === memberCountLong" placement="top">
            <TypographyComponent variant="title-large">{{ memberCount }}</TypographyComponent>
            <template #content>
              <TypographyComponent variant="body-small">{{ memberCountLong }}</TypographyComponent>
            </template>
          </TooltipComponent>
        </FlexComponent>
        <FlexComponent direction="column" align="center" :gap="4" class="segment-list-item__cell">
          <TypographyComponent variant="label-medium" color="var(--secondary-48, #909090)">Created</TypographyComponent>
          <TypographyComponent variant="body-small" text-align="center" white-space="nowrap">
            {{ createdLabel }}
          </TypographyComponent>
          <TypographyComponent
            v-if="segment.createdBy?.username"
            variant="body-small"
            color="var(--secondary-48, #909090)"
            white-space="nowrap"
            class="segment-list-item__author"
          >
            by {{ segment.createdBy.username }}
          </TypographyComponent>
        </FlexComponent>
        <FlexComponent direction="column" align="center" :gap="4" class="segment-list-item__cell">
          <TypographyComponent variant="label-medium" color="var(--secondary-48, #909090)">Last update</TypographyComponent>
          <TypographyComponent variant="body-small" text-align="center" white-space="nowrap">
            {{ updatedLabel }}
          </TypographyComponent>
          <TypographyComponent
            v-if="segment.updatedBy?.username"
            variant="body-small"
            color="var(--secondary-48, #909090)"
            white-space="nowrap"
            class="segment-list-item__author"
          >
            by {{ segment.updatedBy.username }}
          </TypographyComponent>
        </FlexComponent>
        <FlexComponent direction="column" align="center" :gap="4" class="segment-list-item__cell">
          <TypographyComponent variant="label-medium" color="var(--secondary-48, #909090)">Type</TypographyComponent>
          <TypographyComponent variant="title-large" text-align="center" class="segment-list-item__type">
            {{ typeLabel }}
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.segment-list-item {
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);
}
.segment-list-item__main {
  min-width: 0;
  flex: 1 1 260px;
}
.segment-list-item__text {
  min-width: 0;
}
.segment-list-item__name {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}
.segment-list-item__description {
  max-width: 100%;
  overflow: hidden;
}
.segment-list-item__meta {
  flex: 1 1 360px;
}
.segment-list-item__cell {
  flex: 1 1 auto;
  min-width: 72px;
  text-align: center;
}
.segment-list-item__badge {
  flex: 0 0 auto;
  padding: 8px;
  border-radius: 8px;
  white-space: nowrap;
  background: #b8b8b8;
}
.segment-list-item__badge--failed {
  background: var(--alert, #ff5733);
  cursor: default;
}
</style>
